import { useEffect, useMemo, useRef, useState } from "react";
import { Layout } from "@/components/Layout";
import {
  STAGE,
  COLORS,
  NODES,
  WIRES,
  SCENES,
} from "@/explainer/explainerScript";
import {
  Landmark,
  SlidersHorizontal,
  FileText,
  BookOpen,
  Hexagon,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  RotateCcw,
  PencilLine,
  Video,
  Clapperboard,
} from "lucide-react";

const ICONS = {
  landmark: Landmark,
  sliders: SlidersHorizontal,
  file: FileText,
  book: BookOpen,
  hexagon: Hexagon,
};

const NODE_R = 46;

export function wrapText(text, max = 78) {
  const words = text.split(" ");
  const lines = [];
  let line = "";
  words.forEach((w) => {
    if ((line + " " + w).trim().length > max) {
      lines.push(line.trim());
      line = w;
    } else {
      line += " " + w;
    }
  });
  if (line.trim()) lines.push(line.trim());
  return lines;
}

function sceneState(index) {
  const visible = new Set();
  let wiresOn = false;
  for (let i = 0; i <= index; i++) {
    (SCENES[i].showNodes || []).forEach((n) => visible.add(n));
    if (SCENES[i].showWires) wiresOn = true;
  }
  const s = SCENES[index];
  return {
    visible,
    wiresOn,
    pulses: s.pulses || {},
    flows: s.flows || [],
    titleCard: !!s.isTitleCard,
  };
}

function wireEndpoints(wire) {
  const a = NODES.find((n) => n.id === wire.from);
  const b = NODES.find((n) => n.id === wire.to);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  const pad = NODE_R + 10;
  return {
    x1: a.x + (dx / len) * pad,
    y1: a.y + (dy / len) * pad,
    x2: b.x - (dx / len) * pad,
    y2: b.y - (dy / len) * pad,
  };
}

const Wire = ({ wire, flow }) => {
  const { x1, y1, x2, y2 } = wireEndpoints(wire);
  const color = flow ? COLORS[flow.color] : COLORS.wire;
  const [fx1, fy1, fx2, fy2] = flow?.reverse
    ? [x2, y2, x1, y1]
    : [x1, y1, x2, y2];
  return (
    <g>
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={color}
        strokeWidth={flow ? 2.5 : 1.5}
        strokeOpacity={flow ? 0.9 : 0.6}
        style={{ transition: "stroke 400ms, stroke-width 400ms" }}
      />
      {flow && (
        <circle r={6} fill={COLORS[flow.color]}>
          <animate
            attributeName="cx"
            values={`${fx1};${fx2}`}
            dur="1.4s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="cy"
            values={`${fy1};${fy2}`}
            dur="1.4s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0;1;1;0"
            dur="1.4s"
            repeatCount="indefinite"
          />
        </circle>
      )}
    </g>
  );
};

export const PanelCard = ({ panel, animate = true }) => (
  <foreignObject x={20} y={18} width={410} height={220}>
    <div
      data-testid="explainer-panel"
      style={{
        background: "rgba(8,24,49,0.92)",
        border: `1px solid ${COLORS.nodeStroke}`,
        borderRadius: 4,
        padding: "10px 14px",
        fontFamily: "ui-monospace, monospace",
        animation: animate ? "captionEnter 500ms ease-out both" : undefined,
      }}
    >
      <p
        style={{
          color: COLORS.text,
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          margin: 0,
        }}
      >
        {panel.title}
      </p>
      {panel.rows.map((r, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            marginTop: 7,
            animation: animate
              ? `captionEnter 400ms ease-out ${200 + i * 180}ms both`
              : undefined,
          }}
        >
          <span style={{ color: COLORS.subtext, fontSize: 12.5 }}>{r.label}</span>
          {r.status !== "INFO" && (
            <span
              style={{
                color: r.status === "PASS" ? COLORS.green : COLORS.red,
                fontSize: 12.5,
                fontWeight: 700,
              }}
            >
              {r.status}
            </span>
          )}
        </div>
      ))}
    </div>
  </foreignObject>
);

const StageNode = ({ node, pulse }) => {
  const Icon = ICONS[node.icon];
  const glow = pulse ? COLORS[pulse] : null;
  return (
    <g
      data-testid={`explainer-node-${node.id}`}
      style={{ animation: "nodeEnter 600ms ease-out both" }}
    >
      {glow && (
        <circle cx={node.x} cy={node.y} r={NODE_R + 12} fill={glow} opacity={0.18}>
          <animate
            attributeName="r"
            values={`${NODE_R + 6};${NODE_R + 20};${NODE_R + 6}`}
            dur="1.6s"
            repeatCount="indefinite"
          />
        </circle>
      )}
      <circle
        cx={node.x}
        cy={node.y}
        r={NODE_R}
        fill={COLORS.nodeFill}
        stroke={glow || COLORS.nodeStroke}
        strokeWidth={glow ? 3 : 1.5}
        style={{ transition: "stroke 400ms" }}
      />
      <foreignObject
        x={node.x - 18}
        y={node.y - 18}
        width={36}
        height={36}
        style={{ pointerEvents: "none" }}
      >
        <Icon size={36} color={glow || COLORS.text} strokeWidth={1.5} />
      </foreignObject>
      <text
        x={node.x}
        y={node.y + NODE_R + 26}
        textAnchor="middle"
        fill={COLORS.text}
        fontSize={17}
        fontWeight={600}
      >
        {node.label}
      </text>
      <text
        x={node.x}
        y={node.y + NODE_R + 46}
        textAnchor="middle"
        fill={COLORS.subtext}
        fontSize={13}
      >
        {node.sublabel}
      </text>
    </g>
  );
};

export default function Explainer() {
  const [scene, setScene] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!playing) return undefined;
    timerRef.current = setTimeout(() => {
      setScene((s) => {
        if (s >= SCENES.length - 1) {
          setPlaying(false);
          return s;
        }
        return s + 1;
      });
    }, SCENES[scene].duration);
    return () => clearTimeout(timerRef.current);
  }, [scene, playing]);

  const state = useMemo(() => sceneState(scene), [scene]);
  const current = SCENES[scene];
  const flowByWire = Object.fromEntries(state.flows.map((f) => [f.wire, f]));

  return (
    <Layout>
      <style>{`
        @keyframes nodeEnter { from { opacity: 0; transform: scale(0.85); } to { opacity: 1; transform: scale(1); } }
        @keyframes captionEnter { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-slate-400">
            Explainer template
          </p>
          <h1 className="mt-1 text-2xl font-semibold text-slate-900">
            Animated explainer — basic template
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            A minimal skeleton to build your own explainer on. All actors,
            wiring, captions, colours and scene timings live in one script
            file — the full KATLAS video script is kept alongside it as a
            reference you can copy scenes back from.
          </p>
        </div>
      </div>

      {/* Stage */}
      <div
        data-testid="explainer-stage"
        className="relative overflow-hidden rounded-sm border border-slate-200"
        style={{ background: COLORS.bg }}
      >
        <svg
          viewBox={`0 0 ${STAGE.width} ${STAGE.height}`}
          className="block w-full"
        >
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path
                d="M 40 0 L 0 0 0 40"
                fill="none"
                stroke={COLORS.grid}
                strokeWidth="1"
              />
            </pattern>
          </defs>
          <rect width={STAGE.width} height={STAGE.height} fill="url(#grid)" />

          {state.titleCard ? (
            <g style={{ animation: "captionEnter 700ms ease-out both" }}>
              <text
                x={STAGE.width / 2}
                y={STAGE.height / 2 - 20}
                textAnchor="middle"
                fill={COLORS.text}
                fontSize={40}
                fontWeight={700}
              >
                {current.title}
              </text>
              {wrapText(current.body).map((line, i) => (
                <text
                  key={i}
                  x={STAGE.width / 2}
                  y={STAGE.height / 2 + 26 + i * 30}
                  textAnchor="middle"
                  fill={COLORS.subtext}
                  fontSize={20}
                >
                  {line}
                </text>
              ))}
            </g>
          ) : (
            <>
              {state.wiresOn &&
                WIRES.map((w) => (
                  <Wire key={w.id} wire={w} flow={flowByWire[w.id]} />
                ))}
              {NODES.filter((n) => state.visible.has(n.id)).map((n) => (
                <StageNode key={n.id} node={n} pulse={state.pulses[n.id]} />
              ))}
              {current.panel && <PanelCard key={current.id} panel={current.panel} />}
            </>
          )}
        </svg>

        {/* Caption banner */}
        {!state.titleCard && (
          <div
            key={current.id}
            data-testid="explainer-caption"
            className="absolute inset-x-0 bottom-0 border-t px-6 py-4"
            style={{
              background: "rgba(6,17,36,0.88)",
              borderColor: COLORS.grid,
              animation: "captionEnter 500ms ease-out both",
            }}
          >
            <p className="text-sm font-semibold" style={{ color: COLORS.text }}>
              {current.title}
            </p>
            <p className="mt-0.5 text-sm" style={{ color: COLORS.subtext }}>
              {current.body}
            </p>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          data-testid="explainer-restart-btn"
          onClick={() => {
            setScene(0);
            setPlaying(true);
          }}
          className="rounded-sm border border-slate-300 p-2 text-slate-600 hover:bg-slate-100"
          title="Restart"
        >
          <RotateCcw size={16} />
        </button>
        <button
          data-testid="explainer-prev-btn"
          onClick={() => setScene((s) => Math.max(0, s - 1))}
          disabled={scene === 0}
          className="rounded-sm border border-slate-300 p-2 text-slate-600 hover:bg-slate-100 disabled:opacity-40"
          title="Previous scene"
        >
          <SkipBack size={16} />
        </button>
        <button
          data-testid="explainer-play-btn"
          onClick={() => setPlaying((p) => !p)}
          className="rounded-sm bg-blue-950 p-2 text-white hover:bg-blue-900"
          title={playing ? "Pause" : "Play"}
        >
          {playing ? <Pause size={16} /> : <Play size={16} />}
        </button>
        <button
          data-testid="explainer-next-btn"
          onClick={() => setScene((s) => Math.min(SCENES.length - 1, s + 1))}
          disabled={scene === SCENES.length - 1}
          className="rounded-sm border border-slate-300 p-2 text-slate-600 hover:bg-slate-100 disabled:opacity-40"
          title="Next scene"
        >
          <SkipForward size={16} />
        </button>

        <div className="ml-2 flex flex-1 flex-wrap items-center gap-1">
          {SCENES.map((s, i) => (
            <button
              key={s.id}
              data-testid={`explainer-scene-chip-${i}`}
              onClick={() => setScene(i)}
              title={s.title}
              className={`h-2 flex-1 rounded-full transition-colors duration-200 ${
                i === scene
                  ? "bg-blue-950"
                  : i < scene
                    ? "bg-slate-400"
                    : "bg-slate-200 hover:bg-slate-300"
              }`}
              style={{ minWidth: 24 }}
            />
          ))}
        </div>
        <span className="font-mono text-xs text-slate-400">
          scene {scene + 1}/{SCENES.length} — {current.id}
        </span>
      </div>

      {/* Repurpose guide */}
      <div
        data-testid="explainer-repurpose-panel"
        className="mt-10 rounded-sm border border-slate-200 bg-white p-6"
      >
        <p className="font-mono text-xs uppercase tracking-widest text-slate-400">
          How to repurpose this demo
        </p>
        <div className="mt-4 grid gap-6 md:grid-cols-3">
          <div>
            <div className="flex items-center gap-2 text-slate-900">
              <PencilLine size={16} />
              <p className="text-sm font-semibold">1. Edit the script</p>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-slate-500">
              Every actor, wire, caption, colour and scene timing lives in{" "}
              <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-xs">
                src/explainer/explainerScript.js
              </code>
              . Rename roles, rewrite captions, add or remove scenes — the stage
              re-renders automatically. The original KATLAS scenes are in{" "}
              <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-xs">
                explainerScript.katlas.js
              </code>{" "}
              for reference.
            </p>
          </div>
          <div>
            <div className="flex items-center gap-2 text-slate-900">
              <Video size={16} />
              <p className="text-sm font-semibold">2. Screen-record it</p>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-slate-500">
              Press restart, let it auto-play, and capture with Loom, OBS Studio
              or macOS QuickTime (Cmd+Shift+5). Record just the stage area for a
              clean MP4 with no browser chrome.
            </p>
          </div>
          <div>
            <div className="flex items-center gap-2 text-slate-900">
              <Clapperboard size={16} />
              <p className="text-sm font-semibold">3. Render programmatically</p>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-slate-500">
              A Remotion project lives at{" "}
              <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-xs">
                /app/remotion
              </code>{" "}
              and reads the same scene script. Run{" "}
              <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-xs">
                yarn render
              </code>{" "}
              there to export a fresh MP4 — no screen recording needed.
            </p>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/explainer/video`}
              data-testid="explainer-download-video-link"
              className="mt-3 inline-block rounded-sm bg-blue-950 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-900"
            >
              Download latest render (MP4)
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
}
